import type { FuneralAssistanceParseResult, FuneralAssistanceRecord } from '../funeralAssistanceTypes';
import { loadXlsx } from './parserUtils';

type HeaderIndexes = {
    code: number;
    name: number;
    dependents: number;
    feeCount: number;
    amount: number;
};

const NUMBER_FORMAT_REGEX = /[.,]/;

const toNumber = (value: unknown): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const normalized = NUMBER_FORMAT_REGEX.test(value) ? value.replace(/\./g, '').replace(',', '.') : value;
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const isHeaderMatch = (cell: unknown, search: string): boolean => {
    if (typeof cell !== 'string') return false;
    return cell.toLowerCase().includes(search);
};

const resolveIndexes = (headerRow: (string | number | null)[]): HeaderIndexes => {
    const code = headerRow.findIndex((cell) => isHeaderMatch(cell, 'inscri'));
    const name = headerRow.findIndex((cell) => isHeaderMatch(cell, 'nome'));
    const dependents = headerRow.findIndex((cell) => isHeaderMatch(cell, 'qtd dep'));
    const feeCount = headerRow.findIndex((cell) => isHeaderMatch(cell, 'qtd taxa'));
    const amount = headerRow.findIndex((cell) => isHeaderMatch(cell, 'valor'));

    if ([code, name, dependents, feeCount, amount].some((index) => index === -1)) {
        throw new Error('Colunas de inscrição, nome, dependentes, taxa ou valor não encontradas.');
    }

    return { code, name, dependents, feeCount, amount };
};

const normalizeString = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value).trim();
    return '';
};

const parseRows = (
    rows: (string | number | null)[][],
    headerIndex: number,
    indexes: HeaderIndexes,
): FuneralAssistanceRecord[] => {
    const records: FuneralAssistanceRecord[] = [];

    for (let i = headerIndex + 1; i < rows.length; i += 1) {
        const row = rows[i];
        if (!row) continue;
        const rawCode = row[indexes.code];
        const rawName = row[indexes.name];
        const code = normalizeString(rawCode);
        const name = normalizeString(rawName);

        if (!code || !name) continue;
        const numericCode = code.replace(/\D/g, '');
        if (!numericCode) continue;
        const normalizedCode = code.toLowerCase();
        const normalizedName = name.toLowerCase();
        if (
            normalizedCode.includes('empresa') ||
            normalizedName.includes('empresa') ||
            normalizedCode.includes('total') ||
            normalizedName.includes('total')
        ) {
            continue;
        }

        const dependentsCount = toNumber(row[indexes.dependents]) ?? 0;
        const feeCount = toNumber(row[indexes.feeCount]) ?? 0;
        const amount = toNumber(row[indexes.amount]) ?? 0;

        records.push({
            code: numericCode,
            name,
            dependentsCount,
            feeCount,
            amount,
        });
    }

    return records;
};

export async function parseZeloFile(file: File): Promise<FuneralAssistanceParseResult> {
    const xlsx = await loadXlsx();
    const buffer = await file.arrayBuffer();
    const workbook = xlsx.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        throw new Error('Arquivo sem abas válidas.');
    }
    const sheet = workbook.Sheets[sheetName];
    const rows = xlsx.utils.sheet_to_json(sheet, {
        header: 1,
        raw: true,
        defval: null,
    }) as (string | number | null)[][];

    const headerIndex = rows.findIndex((row) => row?.some((cell) => isHeaderMatch(cell, 'inscri')));
    if (headerIndex === -1) {
        throw new Error('Cabeçalho não identificado no arquivo da Zelo.');
    }

    const indexes = resolveIndexes(rows[headerIndex] as (string | number | null)[]);
    const records = parseRows(rows, headerIndex, indexes);

    const totals = records.reduce(
        (acc, record) => {
            acc.dependents += record.dependentsCount;
            acc.feeCount += record.feeCount;
            acc.amount += record.amount;
            return acc;
        },
        { dependents: 0, feeCount: 0, amount: 0 },
    );

    return { records, totals };
}
